import mem, { memClear } from 'mem/dist';

import * as API from '../api/ActivitiesApi';
import { uploadImage } from '../api/ImageApi';
import type {
  Activity,
  Attachment,
  GetAvailabilitiesPayload,
} from '../types/Activity';
import {
  AdminSearchDefaultQuery,
  AdminSearchFilterQuery,
  AdminSearchSort,
  SortCondition,
} from '../api/ActivitiesApi';
import { UploadAttachmentData } from '../components/activities/ActivityAttachments/UploadActivityAttachment';
import RequiredActivitySchema from '../schemas/ActivitySchema';
import { replaceSpaces } from '../infrastructure/utils/common';
import { FareharborItem } from '../types/Fareharbor';

import { memoizedGetSuppliers } from './VendorsService';

export type ActivitiesSearchOrder = {
  name: string;
  up: boolean;
  isString: boolean;
};
export type ActivitiesSearchFilter = {
  featured: 'yes' | 'no' | 'both';
  bookable: 'yes' | 'no' | 'both';
  bachified: 'yes' | 'no' | 'both';
  hidden: 'yes' | 'no' | 'both';
  fareharbor?: 'no';
  singenuity?: 'yes';
  location: string;
  title: string;
  tags: string[];
  price?: string;
};

const filterByFlag = (
  query: AdminSearchFilterQuery,
  filter: ActivitiesSearchFilter,
  field: string
) => {
  if (filter[field] === 'yes')
    query.bool.must.push({ term: { 'tags.keyword': field } });
  else if (filter[field] === 'no')
    query.bool.must_not.push({ term: { 'tags.keyword': field } });
};
const filterByMaxPrice = (query: AdminSearchFilterQuery, price: string) => {
  const range = { 'bookingOptions.price': { gt: price } };
  query.bool.must.push({
    bool: {
      filter: {
        nested: {
          path: 'bookingOptions',
          query: { bool: { must_not: [{ range }] } },
        },
      },
    },
  });
};
const getSortConditions = (order: ActivitiesSearchOrder): AdminSearchSort => {
  const propertyName = order.isString ? `${order.name}.keyword` : order.name;

  const sortCondition: Record<string, SortCondition> = {
    [propertyName]: { order: order.up ? 'ASC' : 'DESC' },
  };

  if (['title', 'city'].includes(order.name)) {
    sortCondition[propertyName].missing = order.up ? '_first' : '_last';
  }

  return [sortCondition];
};
export const getAllActivities = async (
  order: ActivitiesSearchOrder,
  filterData?: ActivitiesSearchFilter,
  from = 0,
  getAll = false,
  projection: string[] = []
): Promise<Activity[]> => {
  let query: AdminSearchDefaultQuery | AdminSearchFilterQuery = {
    match_all: {},
  };
  if (filterData) {
    query = {
      bool: { must: [], must_not: [] },
    };

    filterByFlag(query, filterData, 'featured');
    filterByFlag(query, filterData, 'bookable');
    filterByFlag(query, filterData, 'bachified');
    filterByFlag(query, filterData, 'hidden');

    if (filterData.fareharbor === 'no') {
      query.bool.must_not.push({
        term: { 'source.keyword': 'fareharbor' },
      });
    }

    if (filterData.singenuity === 'yes') {
      query.bool.must.push({
        term: { 'source.keyword': 'singenuity' },
      });
    }

    if (filterData.location.length > 0) {
      query.bool.must.push({
        term: { 'city.keyword': filterData.location },
      });
    }

    if (filterData.title.length >= 3) {
      query.bool.must.push({
        match_phrase_prefix: { title: filterData.title },
      });
    }

    for (const tag of filterData.tags) {
      query.bool.must.push({ term: { 'tags.keyword': tag } });
    }

    if (filterData.price && filterData.price !== '') {
      filterByMaxPrice(query, filterData.price);
    }
  }

  const sort = getSortConditions(order);
  const size = getAll ? 10000 - from : 100;

  return API.adminSearch(query, sort, from, size, projection);
};

export const listActivitiesForAutoComplete = async (
  filterData: Partial<ActivitiesSearchFilter> = {}
): Promise<Activity[]> => {
  const defaultFilter: ActivitiesSearchFilter = {
    featured: 'both',
    bookable: 'both',
    bachified: 'both',
    hidden: 'both',
    location: '',
    title: '',
    tags: [],
  };
  const defaultOrder: ActivitiesSearchOrder = {
    name: 'title',
    up: true,
    isString: true,
  };

  return getAllActivities(
    defaultOrder,
    { ...defaultFilter, ...filterData },
    0,
    false,
    ['id', 'title', 'city']
  );
};

export function getActivityByCity(city, featured) {
  return API.getActivityByCity(city, featured);
}
export function createActivity(activity) {
  const parse = RequiredActivitySchema.parse(activity);
  const { deadline } = parse;
  const { bookingOptions = [] } = activity;

  return API.createActivity({
    ...activity,
    bookingOptions,
    deadline,
    productType: 'experience',
  });
}

export function getById(id) {
  return API.getById(id);
}
export function getSuggestedActivity(id) {
  return API.getSuggestedActivity(id);
}

export function getSourceById(id) {
  return API.getSourceById(id);
}

export function deleteActivity(id) {
  return API.deleteActivity(id);
}
export function uploadPhoto(activityId, pictureId, data) {
  return API.uploadPhoto(activityId, pictureId, data);
}

export function getCities() {
  return API.getCities();
}

export function changeCityStatus(data) {
  return API.changeCityStatus(data);
}

export function deleteCity(name) {
  return API.deleteCity(name);
}

export async function uploadCityImage(
  blob: Blob,
  cityName: string,
  type?: boolean
) {
  const urlData = await API.getUrlForCitiesImage(cityName, type);
  await uploadImage(urlData.uploadUrl, blob);
  return urlData.url;
}

export function getAllTags() {
  return API.getAllTags();
}

export function getByTag(city, tag, price, bookable) {
  return API.getActivityByTag(city, tag, price, bookable);
}

export function updateTag(tag) {
  return API.changeTagType(tag);
}

export function searchByName(name) {
  return API.searchByName(name);
}

export function searchByVendor(id) {
  return API.searchByVendor(id);
}

export function getRnRsByActivityId(id) {
  return API.getRnRsByActivityId(id);
}

export function updateActivity(id, activity) {
  const parse = RequiredActivitySchema.parse(activity);
  const { deadline } = parse;
  return API.updateActivity(id, { ...activity, deadline });
}

export function createPreSignedImgUrl(id, image) {
  return API.createPreSignedImgUrl(id, image);
}

export function uploadActivityImage(id, image) {
  return uploadImage(id, image, true);
}

export function updateBookingOption(activityId, optionId, changedFields) {
  return API.updateBookingOption(activityId, optionId, changedFields);
}

export function getActivityAvgRating(id) {
  return API.getActivityAvgRating(id);
}

export function createBookingOption(id, option) {
  return API.createBookingOption(id, option);
}

export function deleteBookingOption(activityId, optionId) {
  return API.deleteBookingOption(activityId, optionId);
}

export function uploadBulkActivities(activities) {
  return API.uploadBulkActivities(activities);
}

const memoizedGetFHItems = mem(API.getFHItems, { maxAge: 1000 * 60 });
export function getFHItems(): Promise<FareharborItem[]> {
  return memoizedGetFHItems();
}

export function getActivityFromFareharborItem(pk) {
  return API.getActivityFromFareharborItem(pk);
}

export function getBookingOptionAvailabilities(
  payload: GetAvailabilitiesPayload
) {
  return API.getBookingOptionAvailabilities(payload);
}

export const uploadAttachment = async (
  activityId: string,
  attachmentData: Required<UploadAttachmentData>
): Promise<Attachment> => {
  const signedUploadUrl = await API.getSignedUploadUrlForActivity(
    activityId,
    'activity-attachments',
    attachmentData.fileToUpload.type
  );

  await fetch(signedUploadUrl.uploadUrl, {
    method: 'PUT',
    body: attachmentData.fileToUpload,
  });

  return {
    id: signedUploadUrl.id,
    type: attachmentData.type,
    title: replaceSpaces(attachmentData.title),
    mimeType: attachmentData.fileToUpload.type,
    url: signedUploadUrl.url,
    uploadedAt: new Date().toISOString(),
  };
};

export const listCategories = async () => {
  return API.listCategories();
};

export async function syncFHItems() {
  await API.syncFHItems();

  memClear(memoizedGetFHItems);
  memClear(memoizedGetSuppliers);
}
